<template>
  <div class="field-form">
    <c-loader v-if="!inited" :active="!inited" :height="100" />

    <template v-else-if="hasAvailableTypes">
      <form @submit.prevent="submit">
        <div class="form-group field-form__type">
          <c-input v-model="form.fieldType" type="select" label="Поле/блок" :searchable="true" :items="fieldTypes" />
        </div>

        <!-- CUSTOM FIELDS VIEW -->
        <complex-field-form
          :form="complex"
          :field="field"
          :field-type="currentFieldType"
          @editDictionary="onEditDictionary"
        />

        <!-- COMMON PARAMS -->
        <div class="form-group">
          <c-checkbox v-model="form.mobile" type="checkbox" label="Показывать в мобильном приложении" />
        </div>

        <div class="form-group">
          <c-checkbox v-model="form.available" type="checkbox" label="Доступно сотруднику для редактирования" />
        </div>

        <!-- REQUIRED PARAM -->
        <div v-if="canBeRequired" class="form-group">
          <c-checkbox v-model="form.required" type="checkbox" label="Обязательное" />
        </div>

        <!-- LIST PARAMS -->
        <list-params
          v-if="isList"
          :form="form"
          :field-name="currentFieldName"
          :field-type="currentFieldType"
          @change="onEditListDictionary"
        />
      </form>
    </template>
    <div v-else class="no-types">Все системные поля добавлены в анкету</div>
  </div>
</template>

<script>
import CInput from "@/components/controls2/Input";
import CLoader from "@/components/loader";
import CCheckbox from "@/components/controls/switcher";
import ListParams from "@/pages/profile/form/components/ListParams";
import ComplexFieldForm from "./components/ComplexFieldForm";

import { SYSTEM_TYPES, SYSTEM_TYPES_LIST } from "@/domains/profile/constants/system-types";
import { formatProfileFieldModel } from "@/domains/profile/models/ProfileFieldModel";
import { FieldVariant, formatFieldVariant } from "@/domains/profile/models/ProfileFieldVariant";
import { SkillsModel } from "./models/SkillsModel";
import { CareerModel } from "./models/CareerModel";
import { PersonalDataModel } from "./models/PersonalDataModel";

const [defaultFieldType] = SYSTEM_TYPES_LIST;

export default {
  components: {
    CInput,
    CCheckbox,
    CLoader,
    ListParams,
    ComplexFieldForm,
  },
  props: ["field", "saveFieldData", "saveDictionary", "fields"],
  data() {
    return {
      inited: false,
      isSubmitted: false,
      form: {
        fieldType: defaultFieldType,
        required: true,
        mobile: true,
        available: true,

        // lists
        custom: false,
        saveCustom: false,
        variants: [],
      },

      // complex fields
      complex: {
        skills: SkillsModel(),
        career: CareerModel(),
        personalData: PersonalDataModel(),
      },

      changedListVariants: false,
      changedDictionaries: [],
    };
  },
  computed: {
    disabledTypes() {
      const usedSystemFields = this.fields.filter((field) => field.isSystem).map((field) => field.fieldType);
      return [...new Set(usedSystemFields)];
    },
    fieldTypes() {
      return SYSTEM_TYPES_LIST.map((type) => {
        const { value, label } = type;
        const isUsed = this.disabledTypes.includes(value);

        let disabled = isUsed;

        if (this.field && this.field.fieldType === value) disabled = false;

        return {
          value,
          label,
          formatLabel: this.field ? label : `${label} ${isUsed ? "<span class='used'>(добавлено)</span>" : ""}`,
          disabled,
        };
      });
    },
    hasAvailableTypes() {
      const res = this.fieldTypes.some((type) => !type.disabled);
      return res;
    },
    currentFieldType() {
      const type = this.form.fieldType;
      return type && type.value;
    },

    currentFieldConfig() {
      return SYSTEM_TYPES[this.currentFieldType];
    },

    currentFieldName() {
      return this.currentFieldConfig.name;
    },

    canBeRequired() {
      if (!this.currentFieldConfig) return false;
      const { optional, isBoolean, isComplex } = this.currentFieldConfig;
      return !optional && !isBoolean && !isComplex;
    },

    isComplex() {
      if (!this.currentFieldConfig) return false;
      return this.currentFieldConfig.isComplex;
    },

    isList() {
      if (!this.currentFieldConfig) return false;
      return this.currentFieldConfig.isList;
    },

    isValid() {
      return true;
    },
  },
  watch: {
    "form.fieldType": async function (v) {
      if (!this.inited) return;

      this.inited = false;

      if (v) {
        const fieldConfig = this.getFieldConfig(v.value);

        this.form.required = fieldConfig.requiredByDefault;

        this.resetAdditionalValues();
        await this.loadDictionaries(v.value);
      }

      this.inited = true;
    },
  },
  async created() {
    if (this.field) {
      this.setData(this.field);
    } else {
      const fieldType = SYSTEM_TYPES_LIST.find((type) => !this.disabledTypes.includes(type.value));
      if (fieldType) {
        this.form.fieldType = fieldType;
        const fieldConfig = this.getFieldConfig(fieldType.value);
        this.form.required = fieldConfig.requiredByDefault;
      } else this.form.fieldType = null;
    }

    if (this.form.fieldType) {
      await this.loadDictionaries(this.form.fieldType.value);
    }

    this.inited = true;
  },

  methods: {
    getFieldConfig(fieldType) {
      return SYSTEM_TYPES[fieldType];
    },

    getDictionaryByCode(code) {
      return this.$store.dispatch("profile/getDictionary", { code }).then((list) => list.map(FieldVariant));
    },

    async loadComplexTypeDictionaries(list) {
      const dictionaries = {};

      await Promise.all(
        Object.entries(list).map(([key, dictionaryCode]) =>
          this.getDictionaryByCode(dictionaryCode).then((list) => {
            dictionaries[key] = list;
          }),
        ),
      );

      return dictionaries;
    },

    async loadDictionaries(fieldType) {
      const fieldConfig = this.getFieldConfig(fieldType);

      if (fieldConfig.isList) {
        const { dictionaryCode } = fieldConfig;
        const list = await this.getDictionaryByCode(dictionaryCode);
        this.form.variants = list;
      } else if (fieldConfig.isComplex) {
        const { dictionaryCodes } = fieldConfig;
        const dictionaries = await this.loadComplexTypeDictionaries(dictionaryCodes);
        Object.values(this.complex).forEach((model) => model.setDictionaries(dictionaries));
      }
    },

    setData(field) {
      const { fieldType, ...fieldParams } = field;
      this.form.fieldType = { value: fieldType };

      Object.keys(fieldParams).forEach((key) => {
        if (key in this.form) this.form[key] = fieldParams[key];
      });

      Object.values(this.complex).forEach((model) => model.update(field));
    },

    resetAdditionalValues() {
      this.form.custom = false;
      this.form.saveCustom = false;
      this.form.variants = [];

      Object.values(this.complex).forEach((model) => model.reset());
    },

    getParams() {
      const { fieldType, required, ...otherParams } = this.form;
      let fieldParams = {
        isSystem: true,
        fieldType: fieldType.value,
        name: fieldType.label,

        ...otherParams,
      };

      if (this.canBeRequired) {
        fieldParams.required = required;
      }

      Object.values(this.complex).forEach((model) => {
        fieldParams = {
          ...fieldParams,
          ...model.getData(),
        };
      });

      return formatProfileFieldModel(fieldParams);
    },

    getDictionaries() {
      const fieldConfig = this.getFieldConfig(this.currentFieldType);

      if (fieldConfig.isList && this.changedListVariants) {
        return [
          {
            code: fieldConfig.dictionaryCode,
            list: this.form.variants.map(formatFieldVariant),
          },
        ];
      }

      if (fieldConfig.isComplex) {
        let dictionaries = {};

        Object.values(this.complex).forEach((model) => {
          dictionaries = {
            ...dictionaries,
            ...model.getDictionaries(),
          };
        });

        const dictionariesList = [];
        const { dictionaryCodes } = fieldConfig;

        Object.entries(dictionaryCodes).forEach(([key, value]) => {
          if (!this.changedDictionaries.includes(key)) return;

          dictionariesList.push({
            code: value,
            list: dictionaries[key].map(formatFieldVariant),
          });
        });

        return dictionariesList;
      }

      return [];
    },

    async submit() {
      this.isSubmitted = true;
      if (!this.isValid) {
        return;
      }

      this.$emit("pending", true);

      const params = this.getParams();

      let fieldModel = null;
      const fieldRequest = this.saveFieldData(params).then((model) => {
        fieldModel = model;
      });

      const dictionaries = this.getDictionaries();
      const dictionariesRequest = Promise.all(
        dictionaries.map((dictionary) => {
          const { code, list } = dictionary;
          return this.saveDictionary({ dictionary: list, code });
        }),
      );

      await Promise.all([fieldRequest, dictionariesRequest]);

      this.$emit("submitted", fieldModel);
      this.$emit("pending", false);
    },

    reset() {
      this.isSubmitted = false;
      this.form.fieldType = defaultFieldType;
      this.resetAdditionalValues();
    },

    onEditListDictionary() {
      this.changedListVariants = true;
    },

    onEditDictionary(code) {
      this.changedDictionaries.push(code);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars.scss";
.field-form {
  .form-group.checkbox {
    margin-bottom: 20px;
  }
  &__type {
    padding-top: 6px;
  }
}
.no-types {
  color: $label;
}
/deep/ {
  .used {
    font-size: 11px;
  }
}
</style>
